import { useState } from 'react';
import { AuthHeading, AuthPrimaryButton, AuthShell } from './AuthShell';
import { AuthErrorBanner } from './fieldError';
import { GoogleSignupButton } from './GoogleSignupButton';
import type { useAuthActions } from '../hooks/useAuthActions';

const FONT_SANS = 'var(--font-brand)';

/**
 * "Create your account": the step after Google or Microsoft sign-in found no
 * account for the address.
 *
 * Both pages reach it the same way, so a person who clicked Log in and a person
 * who clicked Sign up end up in one place. It asks for one thing, the terms,
 * and has one button. The backend then verifies the provider's ID token itself
 * and creates the account (`/auth/google-signup`, `/auth/microsoft-signup`,
 * behind their flags), and the person is signed in. Only offered when that
 * provider's flag is on, so it never leads to a route that answers 404.
 *
 * Google's button is rendered by Google (its policy and its iframe), so for
 * Google "one button" is Google's own, gated on the terms box.
 */
export function CreateAccountStep({
  actions,
}: {
  actions: ReturnType<typeof useAuthActions>;
}) {
  const create = actions.ssoCreate;
  if (!create) return null;
  return <Step create={create} actions={actions} />;
}

function Step({
  create,
  actions,
}: {
  create: { provider: 'Google' | 'Microsoft'; clientId: string };
  actions: ReturnType<typeof useAuthActions>;
}) {
  const [accepted, setAccepted] = useState(false);
  return (
    <AuthShell dataName="CreateAccount">
      <div className="w-full" style={{ fontFamily: FONT_SANS }}>
        <AuthHeading title="Create your account">
          There is no BIASafe account for this {create.provider} address yet.
          Accept the terms to create one and continue.
        </AuthHeading>

        {actions.error && (
          <AuthErrorBanner className="mt-4">{actions.error}</AuthErrorBanner>
        )}

        <div className="mt-6 flex items-center gap-[10px]">
          <input
            id="sso-accept-terms"
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="size-[16px] shrink-0 cursor-pointer accent-[#121212]"
          />
          <label
            htmlFor="sso-accept-terms"
            className="cursor-pointer text-[15px] leading-[20px] text-[#000000]"
          >
            Accept the{' '}
            <a
              href="https://biasafe.ai/terms-conditions"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium underline hover:text-[#121212]"
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href="https://biasafe.ai/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium underline hover:text-[#121212]"
            >
              Privacy Policy
            </a>
          </label>
        </div>

        <div className="mt-6">
          {create.provider === 'Microsoft' ? (
            <AuthPrimaryButton
              disabled={!accepted || actions.isLoading}
              onClick={() =>
                actions.beginMicrosoftSignup(create.clientId, true)
              }
            >
              {actions.isLoading
                ? 'Creating account...'
                : 'Create account with Microsoft'}
            </AuthPrimaryButton>
          ) : (
            <GoogleSignupButton
              clientId={create.clientId}
              acceptedTerms={accepted}
              resumeWithSecondFactor={actions.resumeWithSecondFactor}
              completeSignIn={actions.completeSignIn}
            />
          )}
        </div>

        <div className="mt-6 flex justify-center">
          <button
            type="button"
            onClick={actions.cancelSsoCreate}
            className="text-[14px] text-[#3e3e3e] hover:underline"
          >
            Use a different account
          </button>
        </div>
      </div>
    </AuthShell>
  );
}
